import { Box, CircularProgress, Alert, MenuItem, Select, FormControl, InputLabel, Stack, Button, Slider, Tooltip as MuiTooltip, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import InfoIcon from '@mui/icons-material/Info';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { registerMap } from 'echarts';
import { ChartCard } from './ChartCard';
import { CHOROPLETH_COLOR_SCHEMES, getTextColorForBackground } from '../../lib/nivoTheme';
import { useAppSelector } from '../../store/hooks';
import { type GeoJSON } from '../../services';
import { type MapDataItem } from '../../schemas/statsSchema';
import { useChartExcelContext } from '../../hooks';
import { buildChoroplethRows } from '../../utils/chart-excel-rows';
import { type ChartExcelData } from '../../utils/chartExcel';

interface MapFormatterParams {
	name: string;
	data?: MapDataItem;
}

/** Defense-in-depth: escape HTML entities for ECharts tooltip innerHTML renderer */
const escapeHtml = (str: string): string =>
	str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

interface ChoroplethQueryResult {
	data: MapDataItem[];
	maxValue: number;
}

export interface BaseChoroplethMapProps {
	height?: number;
	fullHeight?: boolean;
	/** ECharts map registration key (e.g. 'vietnam', 'vietnam-merged') */
	mapKey: string;
	/** Chart title prefix shown before the year */
	titlePrefix: string;
	/** Tooltip text for the info icon */
	infoTooltip: string;
	/** Error message when data loading fails */
	errorMessage: string;
	/** Error message when GeoJSON fails to load */
	geoJsonErrorMessage: string;
	/** Function to load GeoJSON data */
	loadGeoJson: () => Promise<GeoJSON>;
	/** Hook result for choropleth query */
	useChoroplethQuery: (params: { selectedYear: number | null; selectedFaculty?: string | null }) => {
		data: ChoroplethQueryResult | undefined;
		isLoading: boolean;
		error: unknown;
	};
	/** Province name fallback fields used in tooltip/label formatters */
	provinceNameFields?: readonly string[];
}

const DEFAULT_PROVINCE_FIELDS = ['tenTinh', 'name'] as const;

export function BaseChoroplethMap({
	height = 800,
	fullHeight = false,
	mapKey,
	titlePrefix,
	infoTooltip,
	errorMessage,
	geoJsonErrorMessage,
	loadGeoJson,
	useChoroplethQuery,
	provinceNameFields = DEFAULT_PROVINCE_FIELDS,
}: BaseChoroplethMapProps) {
	const theme = useTheme();
	const availableYears = useAppSelector(state => state.filters.years);
	const selectedFaculty = useAppSelector(state => state.filters.selectedFaculty);
	const [selectedYear, setSelectedYear] = useState<number | null>(null);
	const [selectedColorScheme, setSelectedColorScheme] = useState<string>('blue');
	const [geoJson, setGeoJson] = useState<GeoJSON | null>(null);
	const [isLoadingGeoJson, setIsLoadingGeoJson] = useState(true);
	const [geoJsonLoadError, setGeoJsonLoadError] = useState(false);
	const [zoomLevel, setZoomLevel] = useState<number>(1);
	const chartRef = useRef<ReactECharts | null>(null);

	const { data: choroplethData, isLoading: isLoadingData, error } = useChoroplethQuery({
		selectedYear,
		selectedFaculty
	});

	useEffect(() => {
		loadGeoJson()
			.then(data => {
				// ECharts registerMap requires GeoJSON but its type definitions are more restrictive
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				registerMap(mapKey, data as any);
				setGeoJson(data);
				setIsLoadingGeoJson(false);
			})
			.catch((err) => {
				console.error('Failed to load GeoJSON data:', err);
				setGeoJsonLoadError(true);
				setIsLoadingGeoJson(false);
			});
	}, [loadGeoJson, mapKey]);

	useEffect(() => {
		if (choroplethData && selectedYear === null && availableYears.length > 0) {
			setSelectedYear(availableYears[0]);
		}
	}, [choroplethData, availableYears, selectedYear]);

	const chartData_echarts = useMemo(() => {
		if (!choroplethData) return [];
		return choroplethData.data || [];
	}, [choroplethData]);

	const maxValue = useMemo(() => {
		if (!choroplethData) return 100;
		return choroplethData.maxValue || 100;
	}, [choroplethData]);

	const currentColorScheme = useMemo(() => {
		return CHOROPLETH_COLOR_SCHEMES.find(scheme => scheme.id === selectedColorScheme) || CHOROPLETH_COLOR_SCHEMES[0];
	}, [selectedColorScheme]);

	const getProvinceName = useCallback((data: MapDataItem | undefined, fallbackName: string): string => {
		if (!data) return fallbackName;
		for (const field of provinceNameFields) {
			const val = (data as Record<string, unknown>)[field];
			if (typeof val === 'string' && val) return val;
		}
		return fallbackName;
	}, [provinceNameFields]);

	const getTextColor = useCallback((value: number): string => {
		if (!maxValue || value === 0) return theme.palette.text.primary;

		const ratio = value / maxValue;
		const colors = currentColorScheme.colors;

		let colorIndex: number;
		if (ratio === 0) {
			colorIndex = 0;
		} else {
			const step = 1 / (colors.length - 1);
			colorIndex = Math.min(
				Math.floor(ratio / step) + 1,
				colors.length - 1
			);
		}

		const backgroundColor = colors[colorIndex];
		return getTextColorForBackground(backgroundColor);
	}, [maxValue, currentColorScheme, theme]);

	const handleResetZoom = useCallback(() => {
		if (chartRef.current) {
			const chartInstance = chartRef.current.getEchartsInstance();
			chartInstance.setOption({
				series: [{
					zoom: 1,
					center: undefined
				}]
			});
			setZoomLevel(1);
		}
	}, []);

	const handleZoomIn = useCallback(() => {
		if (chartRef.current) {
			const chartInstance = chartRef.current.getEchartsInstance();
			const newZoom = Math.min(zoomLevel + 0.2, 3);
			chartInstance.setOption({
				series: [{
					zoom: newZoom
				}]
			});
			setZoomLevel(newZoom);
		}
	}, [zoomLevel]);

	const handleZoomOut = useCallback(() => {
		if (chartRef.current) {
			const chartInstance = chartRef.current.getEchartsInstance();
			const newZoom = Math.max(zoomLevel - 0.2, 0.5);
			chartInstance.setOption({
				series: [{
					zoom: newZoom
				}]
			});
			setZoomLevel(newZoom);
		}
	}, [zoomLevel]);

	const handleZoomSliderChange = useCallback((_event: Event, value: number | number[]) => {
		if (chartRef.current && typeof value === 'number') {
			const chartInstance = chartRef.current.getEchartsInstance();
			chartInstance.setOption({
				series: [{
					zoom: value
				}]
			});
			setZoomLevel(value);
		}
	}, []);

	const option = useMemo(() => {
		if (!geoJson) return {};

		const labelFormatter = (params: MapFormatterParams) => {
			const data = params.data;
			if (!data) return params.name || '';
			const provinceName = getProvinceName(data, params.name || '');
			const value = data.value ?? 0;
			if (value === 0) return "";
			const textColor = getTextColor(value);
			return `{${textColor === '#ffffff' ? 'light' : 'dark'}|${provinceName}\n${value.toLocaleString('vi-VN')}}`;
		};

		const emphasisFormatter = (params: MapFormatterParams) => {
			const data = params.data;
			if (!data) return params.name || '';
			const provinceName = getProvinceName(data, params.name || '');
			const value = data.value ?? 0;
			if (value === 0) return provinceName;
			const textColor = getTextColor(value);
			return `{${textColor === '#ffffff' ? 'light' : 'dark'}|${provinceName}\n${value.toLocaleString('vi-VN')}}`;
		};

		const richStyles = (fontSize: number) => ({
			light: {
				color: '#ffffff',
				fontWeight: 'bold' as const,
				fontSize,
				fontFamily: "Be Vietnam Pro"
			},
			dark: {
				color: '#000000',
				fontWeight: 'bold' as const,
				fontSize,
				fontFamily: "Be Vietnam Pro"
			}
		});

		return {
			tooltip: {
				trigger: 'item',
				formatter: (params: MapFormatterParams) => {
					const data = params.data;
					if (!data) return '';
					const provinceName = getProvinceName(data, params.name);
					const value = data.value ?? 0;
					return `${escapeHtml(provinceName)}<br/>Số sinh viên: ${value.toLocaleString('vi-VN')}`;
				},
				backgroundColor: theme.palette.background.paper,
				borderColor: theme.palette.divider,
				textStyle: {
					color: theme.palette.text.primary,
				},
			},
			visualMap: {
				min: 0,
				max: maxValue,
				text: ['Cao', 'Thấp'],
				realtime: false,
				calculable: true,
				inRange: {
					color: currentColorScheme.colors,
				},
				textStyle: {
					color: theme.palette.text.primary,
				},
				left: 'left',
				bottom: '10%',
			},
			series: [
				{
					name: 'Sinh viên',
					type: 'map',
					map: mapKey,
					roam: true,
					zoom: zoomLevel,
					emphasis: {
						label: {
							show: true,
							formatter: emphasisFormatter,
							fontFamily: "Be Vietnam Pro",
							fontSize: 12,
							fontWeight: 'bold',
							rich: richStyles(12),
						},
						itemStyle: {
							areaColor: theme.palette.primary.light,
							borderColor: theme.palette.primary.dark,
							borderWidth: 2,
						},
					},
					itemStyle: {
						borderColor: theme.palette.divider,
						borderWidth: 1,
						areaColor: theme.palette.background.default,
					},
					label: {
						show: true,
						formatter: labelFormatter,
						fontSize: 10,
						fontFamily: "Be Vietnam Pro",
						fontWeight: 'bold',
						rich: richStyles(10),
					},
					data: chartData_echarts,
				},
			],
		};
	}, [geoJson, chartData_echarts, maxValue, currentColorScheme, theme, getTextColor, getProvinceName, zoomLevel, mapKey]);

	// The map owns `selectedYear` independently of the dashboard — pass it as the override so
	// exports are labeled with the map's own year, not a `ChartYearProvider` value (there isn't
	// one here; this component never renders inside DashboardPage).
	const excelContext = useChartExcelContext(selectedYear);
	const excelData: ChartExcelData = useMemo(() => ({
		sheetName: titlePrefix,
		rows: buildChoroplethRows(chartData_echarts),
		context: excelContext,
	}), [chartData_echarts, excelContext, titlePrefix]);

	if (isLoadingGeoJson || (selectedYear !== null && isLoadingData)) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" height={200}>
				<CircularProgress />
			</Box>
		);
	}

	if (!isLoadingGeoJson && !isLoadingData && selectedYear !== null && (!choroplethData || choroplethData.data.length === 0)) {
		return (
			<Alert severity="info">Chưa có dữ liệu</Alert>
		);
	}

	if (error) {
		return (
			<Alert severity="error">
				{errorMessage}
			</Alert>
		);
	}

	if (!geoJson || geoJsonLoadError) {
		return (
			<Alert severity="error">
				{geoJsonErrorMessage}
			</Alert>
		);
	}

	return (
		<ChartCard title={`${titlePrefix} - Năm ${selectedYear || ''}`}
			additionalButtons={
				<Stack
					direction="row"
					spacing={1}
					alignItems="center"
				>
					<MuiTooltip title={infoTooltip} arrow placement="bottom">
						<IconButton size="small" sx={{ bgcolor: 'background.paper' }} aria-label="Thông tin bản đồ">
							<InfoIcon fontSize="small" />
						</IconButton>
					</MuiTooltip>

					<IconButton
						size="small"
						onClick={handleZoomOut}
						disabled={zoomLevel <= 0.5}
						sx={{ bgcolor: 'background.paper' }}
						aria-label="Thu nhỏ bản đồ"
					>
						<ZoomOutIcon fontSize="small" />
					</IconButton>

					<Box sx={{ width: 120, bgcolor: 'background.paper', px: 2, py: 1, borderRadius: 1 }}>
						<Slider
							value={zoomLevel}
							onChange={handleZoomSliderChange}
							min={0.5}
							max={3}
							step={0.1}
							valueLabelDisplay="auto"
							valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
							size="small"
						/>
					</Box>

					<IconButton
						size="small"
						onClick={handleZoomIn}
						disabled={zoomLevel >= 3}
						sx={{ bgcolor: 'background.paper' }}
						aria-label="Phóng to bản đồ"
					>
						<ZoomInIcon fontSize="small" />
					</IconButton>

					<Button
						variant="outlined"
						size="small"
						startIcon={<RestartAltIcon />}
						onClick={handleResetZoom}
						sx={{ bgcolor: 'background.paper' }}
					>
						Đặt lại
					</Button>

					<FormControl size="small" sx={{ minWidth: 150, bgcolor: 'background.paper' }}>
						<InputLabel>Bảng màu</InputLabel>
						<Select
							value={selectedColorScheme}
							label="Bảng màu"
							onChange={(e) => setSelectedColorScheme(e.target.value)}
						>
							{CHOROPLETH_COLOR_SCHEMES.map((scheme) => (
								<MenuItem key={scheme.id} value={scheme.id}>
									{scheme.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl size="small" sx={{ minWidth: 150, bgcolor: 'background.paper' }}>
						<InputLabel>Năm tuyển sinh</InputLabel>
						<Select
							value={selectedYear || ''}
							label="Năm tuyển sinh"
							onChange={(e) => {
								const year = Number(e.target.value);
								setSelectedYear(year);
							}}
						>
							{availableYears.map((year) => (
								<MenuItem key={year} value={year}>
									{year}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Stack>
			}
			height={height}
			fullHeight={fullHeight}
			excelData={excelData}>
			<Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
				<ReactECharts
					ref={chartRef}
					option={option}
					style={{ height: '100%', width: '100%' }}
					notMerge={true}
					lazyUpdate={true}
				/>
			</Box>
		</ChartCard>
	);
}
